const STOP_ID_PREFIX = "stop";

export const generateStopId = () => {
  const rand = Math.random().toString(36).slice(2, 10);
  const time = Date.now().toString(36);
  return `${STOP_ID_PREFIX}_${time}_${rand}`;
};

const toNights = (value, fallback = 1) => {
  const n = parseInt(value, 10);
  if (!Number.isFinite(n) || n < 0) return fallback;
  return n;
};

const pickDestinationFields = (dest) => {
  if (!dest || typeof dest !== "object") return {};
  // Strip id/nights/kind from `rest` so a later spread can never silently
  // overwrite a freshly generated stop id (this caused two stops with the
  // same source location to collide on a shared place_id).
  const {
    id: _ignoredId,
    nights: _ignoredNights,
    kind: _ignoredKind,
    name = null,
    lat = null,
    lon = null,
    latitude = null,
    longitude = null,
    country = null,
    countryCode = null,
    type = null,
    ...rest
  } = dest;
  return {
    name,
    lat: lat ?? latitude,
    lon: lon ?? longitude,
    country,
    countryCode,
    type,
    extra: rest,
  };
};

export const destinationsToStops = (destinations, customNightsMap = {}) => {
  if (!Array.isArray(destinations) || destinations.length === 0) return [];

  const counts = {};
  destinations.forEach((d) => {
    const name = d && d.name != null ? d.name : null;
    if (name) counts[name] = (counts[name] || 0) + 1;
  });
  const distributed = {};

  return destinations.map((dest) => {
    const picked = pickDestinationFields(dest);
    const existingId =
      dest && typeof dest === "object" && typeof dest.id === "string"
        ? dest.id
        : null;
    
    let nightsRaw = undefined;
    if (customNightsMap && picked.name != null && counts[picked.name]) {
      const totalNights = parseInt(customNightsMap[picked.name], 10);
      if (Number.isFinite(totalNights)) {
         const base = Math.floor(totalNights / counts[picked.name]);
         const rem = totalNights % counts[picked.name];
         const index = distributed[picked.name] || 0;
         nightsRaw = base + (index < rem ? 1 : 0);
         distributed[picked.name] = index + 1;
      }
    }
    
    const nights = toNights(nightsRaw, 1);

    return {
      name: picked.name,
      lat: picked.lat,
      lon: picked.lon,
      country: picked.country,
      countryCode: picked.countryCode,
      type: picked.type,
      ...picked.extra,
      id: existingId || generateStopId(),
      nights,
    };
  });
};

export const locationToStop = (location, nights = 1) => {
  const picked = pickDestinationFields(location);
  return {
    name: picked.name,
    lat: picked.lat,
    lon: picked.lon,
    country: picked.country,
    countryCode: picked.countryCode,
    type: picked.type,
    ...picked.extra,
    id: generateStopId(),
    nights: toNights(nights, 1),
  };
};

export const stopsToCustomNightsMap = (stops) => {
  if (!Array.isArray(stops) || stops.length === 0) return {};

  const map = {};
  stops.forEach((stop) => {
    if (!stop || stop.name == null) return;
    const nights = toNights(stop.nights, 0);
    if (map[stop.name] === undefined) {
      map[stop.name] = nights;
    } else {
      map[stop.name] += nights;
    }
  });
  return map;
};

export const stopsToDestinations = (stops) => {
  if (!Array.isArray(stops) || stops.length === 0) return [];

  return stops.map((stop) => {
    const {
      nights: _nights,
      extra: _extra,
      ...rest
    } = stop || {};
    return { ...rest };
  });
};

export const syncStopsIntoFormData = (prev, nextStops) => {
  const destinations = stopsToDestinations(nextStops);
  const destinationStr = destinations.map((d) => d.name).join(" → ");
  return {
    ...prev,
    stops: nextStops,
    destinations,
    destination: destinationStr,
    destinationData: destinations[0] || null,
    customNightsPerDestination: stopsToCustomNightsMap(nextStops),
  };
};

export const hydrateStopsFromTrip = (trip) => {
  if (!trip || typeof trip !== "object") return [];
  if (Array.isArray(trip.stops) && trip.stops.length > 0) {
    const seen = new Set();
    return trip.stops.map((stop) => {
      let id = typeof stop?.id === "string" ? stop.id : null;
      if (!id || seen.has(id)) id = generateStopId();
      seen.add(id);
      return { ...stop, id, nights: toNights(stop?.nights, 1) };
    });
  }
  return destinationsToStops(
    trip.destinations || [],
    trip.customNightsPerDestination || {},
  );
};

export const stopHasName = (stops, name) => {
  if (!Array.isArray(stops) || !name) return false;
  const needle = String(name).toLowerCase();
  return stops.some(
    (s) => s && typeof s.name === "string" && s.name.toLowerCase() === needle,
  );
};
